import { useMutation, useQuery } from '@apollo/client';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import React, { useRef, useState } from 'react';

import { IoMdClose, IoMdTrash } from 'react-icons/io';
import { FAILED } from '../../../Constants/constants';
import { DELETE_PROCESSING_QUEUE_ITEM } from '../../../GraphQL/ManageCustomScopes/Mutations';
import { GET_PROCESSING_SCOPES } from '../../../GraphQL/ManageCustomScopes/Queries';
import GraphQLWrapper from '../../UI/GraphQLWrapper/GraphQLWrapper';
import CustomModal from '../../UI/Modal/Modal';
import './ProcessingQueue.css';

function ProcessingQueue() {
  const [scopes, setScopes] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const selectedToDeleteId = useRef();
  const isFirstRender = useRef(true);

  const { loading, refetch } = useQuery(GET_PROCESSING_SCOPES, {
    notifyOnNetworkStatusChange: true,
    pollInterval: 15000,
    onCompleted: (data) => {
      const formattedScopes = data.trfxCodelessOperationFindMany.map(
        (scope) => {
          return {
            ...scope,
            implementedWC: scope.implementedWC?.name,
            actions: scope._id,
          };
        }
      );
      setScopes(formattedScopes);
      isFirstRender.current = false;
    },
  });

  const [deleteTest] = useMutation(DELETE_PROCESSING_QUEUE_ITEM);

  const onClickDeleteIcon = (id) => {
    setShowDeleteModal(true);
    selectedToDeleteId.current = id;
  };

  const onDelete = () => {
    setShowDeleteModal(false);
    window.showMessage('Loading changes', 'loading');

    deleteTest({ variables: { id: selectedToDeleteId.current } })
      .then(() => {
        refetch().then(() => {
          window.flash('Element was successfully deleted!', 'success');
          window.hideMessage();
          selectedToDeleteId.current = '';
        });
      })
      .catch((error) => {
        window.flash(
          'An error occurred. Test execution could not be deleted',
          'error'
        );
      });
  };

  const actionBodyTemplate = (rowData) => {
    if (rowData.status === FAILED) {
      return (
        <IoMdTrash
          onClick={() => onClickDeleteIcon(rowData._id)}
          className='action-icon action-icon-active'
          size='20'
          title='delete element'
        />
      );
    }

    return (
      <IoMdTrash
        className='action-icon action-icon-disabled'
        size='20'
        title='delete element'
      />
    );
  };

  const statusBodyTemplate = (rowData) => (
    <span className={`status-badge ${rowData.status}`}>{rowData.status}</span>
  );

  const view = (
    <div className='d-flex justify-content-center'>
      <div className='processing-queue-card'>
        <div className='close-button-container'>
          <a href='/view-custom-scopes' className='close-link'>
            <IoMdClose size='20' className='mt-2 mr-2' />
          </a>
        </div>
        <div className='card-content'>
          <div className='d-flex justify-content-center'>
            <div className='table-container'>
              <DataTable
                value={scopes}
                className='processing-queue-table'
                emptyMessage='No processing items found'
                responsiveLayout='scroll'
              >
                <Column field='implementedWC' header='Component' sortable />
                <Column field='operation' header='Operation' sortable />
                <Column
                  field='status'
                  header='Status'
                  body={statusBodyTemplate}
                  sortable
                />
                <Column field='name' header='Custom Scope' sortable />
                <Column
                  field='actions'
                  header='Actions'
                  body={actionBodyTemplate}
                  style={{ width: '100px', textAlign: 'center' }}
                />
              </DataTable>
            </div>
          </div>
          <CustomModal
            show={showDeleteModal}
            title='Are you sure you want to delete this element?'
            onClose={() => setShowDeleteModal(false)}
            onAccept={() => onDelete()}
          />
        </div>
      </div>
    </div>
  );

  return (
    <GraphQLWrapper
      loading={loading && isFirstRender.current}
      component={view}
    />
  );
}

export default ProcessingQueue;
